import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { studentMasterAPI } from '../../services/api';
import './Modal.css';

const AddStudentModal = ({ onClose, onSuccess, userSchoolCode }) => {
  const [formData, setFormData] = useState({
    name: '',
    ic: '',
    dob: '',
    jantina: '',
    citizenStatus: '',
    schoolCode: userSchoolCode || '',
    enrollYear: new Date().getFullYear(),
    tahun: '',
    kelas: ''
  });

  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTransferMode, setIsTransferMode] = useState(false); // Track if this is a transfer-in

  // Dropdown lists
  const [enrollYearsList, setEnrollYearsList] = useState([]);
  const [tahunsList, setTahunsList] = useState([]);
  const [kelasList, setKelasList] = useState([]);

  useEffect(() => {
    loadDropdowns();
  }, [formData.schoolCode, formData.tahun]);

  const loadDropdowns = async () => {
    try {
      const [enrollYears, tahuns, kelas] = await Promise.all([
        studentMasterAPI.getEnrollmentYears(formData.schoolCode || null),
        studentMasterAPI.getTahuns(formData.schoolCode || null),
        studentMasterAPI.getKelas(formData.schoolCode || null, formData.tahun || null)
      ]);

      if (enrollYears.success) {
        const years = enrollYears.data || [];
        setEnrollYearsList(years);
        // Auto-select current year if available
        if (years.length > 0 && !formData.enrollYear) {
          setFormData(prev => ({ ...prev, enrollYear: years[0] }));
        }
      }

      if (tahuns.success) setTahunsList(tahuns.data || []);
      if (kelas.success) setKelasList(kelas.data || []);
    } catch (error) {
      console.error('Failed to load dropdowns:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear validation and transfer mode if IC changes
    if (field === 'ic') {
      setValidationResult(null);
      setIsTransferMode(false);
    }
  };

  const handleValidateIC = async () => {
    const ic = formData.ic.replace(/-/g, '');

    // Validate IC format (12 digits)
    if (!/^\d{12}$/.test(ic)) {
      Swal.fire('Format Tidak Sah', 'No. KP mestilah 12 digit', 'error');
      return;
    }

    if (!formData.schoolCode) {
      Swal.fire('Maklumat Tidak Lengkap', 'Sila pilih sekolah', 'warning');
      return;
    }

    if (!formData.enrollYear) {
      Swal.fire('Maklumat Tidak Lengkap', 'Sila pilih tahun pendaftaran', 'warning');
      return;
    }

    setIsValidating(true);
    try {
      const response = await studentMasterAPI.validateIC({
        ic: ic,
        schoolCode: formData.schoolCode,
        enrollYear: formData.enrollYear
      });

      if (response.success) {
        const result = response.data;
        setValidationResult(result);

        // Auto-fill DOB if extracted from IC (new student)
        if (result.extractedDOB && !formData.dob) {
          const dobDate = new Date(result.extractedDOB);
          const dobString = dobDate.toISOString().split('T')[0];
          setFormData(prev => ({ ...prev, dob: dobString }));
        }

        // Auto-populate from existing student (Transfer In mode)
        if (result.existingStudent) {
          setIsTransferMode(true);
          const student = result.existingStudent;

          setFormData(prev => ({
            ...prev,
            name: student.name || prev.name,
            dob: student.dob ? new Date(student.dob).toISOString().split('T')[0] : prev.dob,
            jantina: student.jantina || prev.jantina,
            citizenStatus: student.citizenStatus || prev.citizenStatus,
            enrollYear: new Date().getFullYear() // Lock to current year
          }));
        } else {
          setIsTransferMode(false);
        }

        // Show status message
        if (result.status === 'IC_NOT_IN_MASTER_LIST') {
          Swal.fire('IC Tidak Dijumpai', result.message, 'error');
        } else if (result.status === 'NEW_IC') {
          if (result.existingStudent) {
            // Transfer In scenario
            Swal.fire('Pemindahan Masuk', 'Pelajar dijumpai dalam Senarai Induk KPMKR. Sila pilih Tahun dan Kelas.', 'info');
          } else {
            // New student scenario
            Swal.fire('Sah', result.message, 'success');
          }
        } else if (result.status === 'ALREADY_ENROLLED') {
          Swal.fire('Amaran', result.message, 'warning');
        } else if (result.status === 'ENROLLED_OTHER_SCHOOL') {
          // Cross-school conflict - student enrolled at another school for same year
          // Show confirmation dialog
          const confirmResult = await Swal.fire({
            title: 'Konflik Pendaftaran',
            html: `
              <div style="text-align: left;">
                <p><strong>Pelajar sudah didaftarkan di sekolah lain:</strong></p>
                <p style="margin-left: 20px;">
                  <i class="fas fa-school"></i> ${result.conflictingSchoolCode} - ${result.conflictingSchoolName || 'Unknown'}
                </p>
                <p style="margin-top: 15px;"><strong>Tahun Pendaftaran:</strong> ${formData.enrollYear}</p>
                <hr style="margin: 15px 0;"/>
                <p style="color: #856404;">
                  <i class="fas fa-exclamation-triangle"></i>
                  Jika diteruskan, pendaftaran di sekolah tersebut akan dinyahaktifkan dan notifikasi akan dihantar.
                </p>
              </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya, Teruskan Pendaftaran',
            cancelButtonText: 'Batal',
            confirmButtonColor: '#d33'
          });

          if (!confirmResult.isConfirmed) {
            // User cancelled - reset validation result so they can't proceed
            setValidationResult(null);
            return;
          }
          // User confirmed - validation result stays, they can proceed with form submission
        } else if (result.status === 'DIFFERENT_SCHOOL') {
          Swal.fire({
            title: 'Pelajar Wujud',
            html: `${result.message}<br><br>Adakah anda ingin meneruskan pendaftaran (pemindahan)?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ya, Teruskan',
            cancelButtonText: 'Batal'
          });
        } else if (result.status === 'GRADUATED') {
          Swal.fire('Maklumat', result.message, 'info');
        } else if (result.status === 'INACTIVE_ENROLLMENT') {
          Swal.fire({
            title: 'Pendaftaran Tidak Aktif',
            html: `${result.message}<br><br>Adakah anda ingin mengaktifkan semula?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Ya, Aktifkan',
            cancelButtonText: 'Batal'
          });
        } else if (result.status === 'INVALID_FORMAT') {
          Swal.fire('Format Tidak Sah', result.message, 'error');
        }
      } else {
        Swal.fire('Ralat', response.message || 'Gagal mengesahkan No. KP', 'error');
      }
    } catch (error) {
      console.error('Validation error:', error);
      Swal.fire('Ralat', 'Ralat semasa mengesahkan No. KP', 'error');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      Swal.fire('Maklumat Tidak Lengkap', 'Sila masukkan nama pelajar', 'warning');
      return;
    }

    const ic = formData.ic.replace(/-/g, '');
    if (!/^\d{12}$/.test(ic)) {
      Swal.fire('Format Tidak Sah', 'No. KP mestilah 12 digit', 'error');
      return;
    }

    if (!formData.schoolCode || !formData.enrollYear || !formData.tahun || !formData.kelas) {
      Swal.fire('Maklumat Tidak Lengkap', 'Sila lengkapkan semua maklumat pendaftaran', 'warning');
      return;
    }

    // Check if IC validated
    if (!validationResult) {
      const result = await Swal.fire({
        title: 'No. KP Belum Disahkan',
        text: 'Anda perlu sahkan No. KP dahulu. Adakah anda ingin sahkan sekarang?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Ya, Sahkan',
        cancelButtonText: 'Batal'
      });

      if (result.isConfirmed) {
        await handleValidateIC();
        return; // Don't submit yet, let user review validation result
      } else {
        return;
      }
    }

    // Check if can proceed based on validation status
    if (!validationResult.canProceed) {
      Swal.fire('Tidak Dapat Diteruskan', validationResult.message, 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await studentMasterAPI.createStudent({
        name: formData.name.trim(),
        ic: ic,
        dob: formData.dob || null,
        jantina: formData.jantina || null,
        citizenStatus: formData.citizenStatus || null,
        schoolCode: formData.schoolCode,
        enrollYear: formData.enrollYear,
        tahun: formData.tahun,
        kelas: formData.kelas
      });

      if (response.success) {
        Swal.fire('Berjaya', 'Pelajar berjaya ditambah', 'success');
        onSuccess();
      } else {
        Swal.fire('Gagal', response.message || 'Gagal menambah pelajar', 'error');
      }
    } catch (error) {
      console.error('Create student error:', error);
      Swal.fire('Ralat', 'Ralat semasa menambah pelajar', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatICInput = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as XXXXXX-XX-XXXX
    if (digits.length <= 6) {
      return digits;
    } else if (digits.length <= 8) {
      return `${digits.slice(0, 6)}-${digits.slice(6)}`;
    } else {
      return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 12)}`;
    }
  };

  const handleICChange = (e) => {
    const formatted = formatICInput(e.target.value);
    handleInputChange('ic', formatted);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Tambah Pelajar Baru</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* IC Section */}
            <div className="form-section">
              <h3>No. Kad Pengenalan</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>No. KP *</label>
                  <div className="ic-input-group">
                    <input
                      type="text"
                      value={formData.ic}
                      onChange={handleICChange}
                      placeholder="XXXXXX-XX-XXXX"
                      maxLength={14}
                      required
                    />
                    <button
                      type="button"
                      className="btn-validate"
                      onClick={handleValidateIC}
                      disabled={isValidating}
                    >
                      {isValidating ? 'Mengesahkan...' : 'Sahkan'}
                    </button>
                  </div>
                  {validationResult && (
                    <div className={`validation-status ${validationResult.canProceed ? 'success' : 'warning'}`}>
                      {validationResult.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Personal Information */}
            <div className="form-section">
              <h3>Maklumat Peribadi</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Nama Penuh * {isTransferMode && <span className="field-locked">(Dari Senarai Induk)</span>}</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Nama penuh pelajar"
                    disabled={isTransferMode}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tarikh Lahir {isTransferMode && <span className="field-locked">(Dari Senarai Induk)</span>}</label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) => handleInputChange('dob', e.target.value)}
                    disabled={isTransferMode}
                  />
                </div>

                <div className="form-group">
                  <label>Jantina {isTransferMode && <span className="field-locked">(Dari Senarai Induk)</span>}</label>
                  <select
                    value={formData.jantina}
                    onChange={(e) => handleInputChange('jantina', e.target.value)}
                    disabled={isTransferMode}
                  >
                    <option value="">Pilih Jantina</option>
                    <option value="L">Lelaki</option>
                    <option value="P">Perempuan</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status Warganegara {isTransferMode && <span className="field-locked">(Dari Senarai Induk)</span>}</label>
                  <select
                    value={formData.citizenStatus}
                    onChange={(e) => handleInputChange('citizenStatus', e.target.value)}
                    disabled={isTransferMode}
                  >
                    <option value="">Pilih Status</option>
                    <option value="WARGANEGARA">Warganegara</option>
                    <option value="PEMASTAUTIN TETAP">Pemastautin Tetap</option>
                    <option value="BUKAN WARGANEGARA">Bukan Warganegara</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Enrollment Information */}
            <div className="form-section">
              <h3>Maklumat Pendaftaran</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Kod Sekolah *</label>
                  <input
                    type="text"
                    value={formData.schoolCode}
                    onChange={(e) => handleInputChange('schoolCode', e.target.value)}
                    placeholder="Cth: SK001"
                    disabled={!!userSchoolCode} // Disable if user has school code
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Tahun Pendaftaran * {isTransferMode && <span className="field-locked">(Tahun Semasa)</span>}</label>
                  <select
                    value={formData.enrollYear}
                    onChange={(e) => handleInputChange('enrollYear', parseInt(e.target.value))}
                    disabled={isTransferMode}
                    required
                  >
                    <option value="">Pilih Tahun</option>
                    {enrollYearsList.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tahun *</label>
                  <select
                    value={formData.tahun}
                    onChange={(e) => handleInputChange('tahun', e.target.value)}
                    required
                  >
                    <option value="">Pilih Tahun</option>
                    {tahunsList.map(tahun => (
                      <option key={tahun} value={tahun}>{tahun}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Kelas *</label>
                  <select
                    value={formData.kelas}
                    onChange={(e) => handleInputChange('kelas', e.target.value)}
                    required
                  >
                    <option value="">Pilih Kelas</option>
                    {kelasList.map(kelas => (
                      <option key={kelas} value={kelas}>{kelas}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Batal
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddStudentModal;
